import { motion } from "framer-motion";
import { CheckCircle, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { localBusiness, businessRef } from "@/lib/business";

export default function Team() {
  // Previously a bare LocalBusiness with a name and nothing else — no @id, no
  // telephone, no sameAs — so it read as a seventh, unrelated business.
  const schema = {
    "@context": "https://schema.org",
    ...localBusiness({
      employee: [
        {
          "@type": "Person",
          "name": "Cameron Versteegh",
          "jobTitle": "Building Inspector",
          "worksFor": businessRef
        }
      ]
    })
  };

  const values = [
    {
      title: "Safety and Compliance",
      description: "Every inspection follows strict safety protocols and compliance standards to protect your property and occupants."
    },
    {
      title: "Clear Communication",
      description: "You receive straightforward updates, accurate estimates, and reports delivered on time without jargon."
    },
    {
      title: "Accountability",
      description: "We take ownership of every job from start to finish and stand behind our work with full transparency."
    },
    {
      title: "Respect for Tenants and Occupants",
      description: "We understand properties are homes and workplaces. Our team works respectfully with minimal disruption."
    },
    {
      title: "Community Connection and Local Capability",
      description: "Darwin based and Darwin focused. We understand NT properties and support our local community."
    }
  ];

  return (
    <div>
      <SEO
        title="Meet Our Team"
        description="Meet the Aussie Inspections team in Darwin NT. Local experts in building inspections, property reports, and maintenance coordination."
        keywords="building inspectors Darwin, property inspection team NT, Cameron Versteegh, Darwin inspection professionals"
        url="/team"
        schema={schema}
      />

      {/* CTA Banner */}
      <CTABanner />

      {/* Hero Section */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-chivo text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-6">
              Meet the Team
            </h1>
            <p className="text-xl text-slate-600 mb-4">
              Hands on operators who are safety focused and responsive
            </p>
            <p className="text-lg text-slate-600">
              Our team brings together trade experience, property management expertise, and local NT knowledge. We handle inspections, estimating, and coordination with a focus on clear communication and practical solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Profile: Cameron */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <img
                src="/team/cameron.jpg"
                alt="Cameron Versteegh, Building Inspector in Darwin NT"
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>

            {/* Bio and Snapshot */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-8"
            >
              {/* Header */}
              <div>
                <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-2">
                  Cameron Versteegh
                </h2>
                <p className="text-xl text-primary font-semibold">
                  Building Inspector
                </p>
              </div>

              {/* Bio */}
              <div className="prose prose-lg prose-slate max-w-none">
                <p>
                  Cameron is a qualified carpenter and Building Inspector with over six years of hands-on trade experience across residential and commercial construction, renovations, maintenance, and site supervision. His practical background provides a strong understanding of building systems, construction sequencing, and common areas of deterioration, allowing him to carry out thorough, methodical visual inspections grounded in real-world building knowledge.
                </p>
                <p>
                  Alongside his trade experience, Cameron has extensive coordination and inspection-related experience, including routine condition assessments, contractor oversight, asset management, and detailed reporting. This combination of field-based inspection work and office-based documentation has developed a disciplined approach to accuracy, consistency, and clear communication—critical elements in producing reliable inspection reports.
                </p>
                <p>
                  Cameron’s inspection approach is structured, independent, and risk-focused. Clients can expect clear observations, well-documented findings, and practical insight presented in a professional and measured manner. His work is underpinned by a strong safety mindset, attention to detail, and a commitment to providing factual, easy-to-understand information to support informed property decisions.
                </p>
              </div>


            </motion.div>
          </div>
        </div>
      </section>



      {/* How We Work */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-chivo text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              How We Work
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              When you contact us, we start with triage to understand your needs. We schedule an inspection at a time that works for you, assess the property, and document visible issues with photos. From there, we prepare a clear scope of work and provide an accurate estimate. Once approved, we coordinate scheduling and any required trades. Throughout the job, we maintain safety protocols, keep you updated, and complete the work to standard. You receive a final report with all documentation and can expect prompt responses if follow up is needed. Our process is designed to be client friendly and credible from start to finish.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Values and Commitments
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg border border-slate-200"
              >
                <CheckCircle className="w-10 h-10 text-secondary mb-4" />
                <h3 className="font-chivo text-xl font-bold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo text-4xl font-bold mb-6">
              Ready to Work With Our Team?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Request a quote, book an inspection, or discuss an ongoing maintenance plan
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
                data-testid="team-cta-contact"
              >
                <Link to="/contact">Get a Quote</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold tracking-wide uppercase px-12"
              >
                <a href="tel:+61413245519">Call Us Today</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
